import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Icon Components
const CrownIcon = () => (
  <svg className="billing-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L15 8L21 9L16 14L18 21L12 17L6 21L8 14L3 9L9 8L12 2Z" />
  </svg>
);

const SparkIcon = () => (
  <svg className="billing-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg className="billing-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="billing-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showPortalBanner, setShowPortalBanner] = useState(null);

  useEffect(() => {
    fetchUserInfo();
    
    // Check for checkout success parameter
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success') {
      setShowSuccessBanner(true);
      // Remove the query parameter from URL without page reload
      setSearchParams({});
    }
    
    // Check for portal return parameters
    const portalStatus = searchParams.get('portal');
    if (portalStatus === 'subscription_updated' || portalStatus === 'subscription_canceled') {
      setShowPortalBanner(portalStatus);
      // Remove the query parameter from URL without page reload
      setSearchParams({});
      
      // Refetch user info immediately and with delays to catch Stripe webhook updates
      fetchUserInfo();
      setTimeout(() => fetchUserInfo(), 2000);
      setTimeout(() => fetchUserInfo(), 5000);
    }
  }, []);

  const fetchUserInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}/me`, { method: 'GET' });

      if (!response.ok) {
        throw new Error('Failed to fetch user information');
      }

      const data = await response.json();
      setUserInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async (flowType = null) => {
    setPortalLoading(true);
    setError('');
    try {
      const returnUrl = window.location.origin + '/billing';
      
      const requestBody = { return_url: returnUrl };
      if (flowType) {
        requestBody.flow_type = flowType;
      }
      
      const response = await apiFetch(`${API_BASE_URL}/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create portal session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Customer Portal
      window.location.href = data.portal_url;
    } catch (err) {
      setError(err.message);
      if (err.message && err.message.includes('No active subscription found')) {
        fetchUserInfo();
      }
      setPortalLoading(false);
    }
  };

  const getPlanDisplay = (planType) => {
    if (planType === 'unlimited' || planType === 'premium') return 'Premium';
    if (planType === 'pro') return 'Pro';
    return 'Free';
  };

  const getBillingCycleDisplay = (billingCycle) => {
    if (billingCycle === 'monthly') return 'Monthly';
    if (billingCycle === 'yearly') return 'Yearly';
    return null;
  };

  const getStatusDisplay = (status) => {
    if (!status) return null;
    if (status === 'active') return 'Active';
    if (status === 'trialing') return 'Trial';
    if (status === 'canceled') return 'Canceled';
    if (status === 'past_due') return 'Past Due';
    if (status === 'unpaid') return 'Unpaid';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const hasActiveSubscription = () => {
    if (!userInfo) return false;
    // User has no active subscription if:
    // - plan_type is "free"
    // - stripe_subscription_status is "canceled"
    // - stripe_subscription_id is null/undefined
    if (userInfo.plan_type === 'free') return false;
    if (userInfo.stripe_subscription_status === 'canceled') return false;
    if (!userInfo.stripe_subscription_id) return false;
    return true;
  };

  const renderUpgradeSection = () => {
    if (!userInfo) return null;

    // Premium users with active subscription - show confirmation
    if (userInfo.plan_type === 'unlimited' && hasActiveSubscription()) {
      return (
        <div className="billing-premium-confirmation">
          <div className="billing-premium-icon-wrapper">
            <CrownIcon />
          </div>
          <div className="billing-premium-content">
            <h3 className="billing-premium-title">You Have Full Access</h3>
            <p className="billing-premium-text">
              You're on the Premium plan with unlimited scans, portfolio tracking, and all advanced features.
            </p>
          </div>
        </div>
      );
    }

    // Pro users with active subscription - subtle Premium upgrade CTA
    if (userInfo.plan_type === 'pro' && hasActiveSubscription()) {
      return (
        <div className="billing-upgrade-section">
          <div className="billing-upgrade-header">
            <div className="billing-upgrade-icon-wrapper">
              <SparkIcon />
            </div>
            <div className="billing-upgrade-header-text">
              <h3 className="billing-upgrade-title">Upgrade to Premium</h3>
              <p className="billing-upgrade-text">
                Unlock unlimited scans, advanced analytics, and the full premium toolkit.
              </p>
            </div>
          </div>
          <ul className="billing-upgrade-features">
            <li>Unlimited stock scans</li>
            <li>Advanced portfolio analytics</li>
            <li>Priority support</li>
          </ul>
          <div className="billing-upgrade-actions">
            <Link to="/pricing" className="btn-billing-upgrade">
              View Premium Plans
            </Link>
          </div>
        </div>
      );
    }

    // No active subscription - upgrade CTA
    if (!hasActiveSubscription()) {
      return (
        <div className="billing-upgrade-section">
          <div className="billing-upgrade-header">
            <div className="billing-upgrade-icon-wrapper">
              <ArrowUpIcon />
            </div>
            <div className="billing-upgrade-header-text">
              <h3 className="billing-upgrade-title">Choose a Plan</h3>
              <p className="billing-upgrade-text">
                Unlock more scans, portfolio tracking, and advanced features with Pro or Premium.
              </p>
            </div>
          </div>
          <ul className="billing-upgrade-features">
            <li>More monthly scans</li>
            <li>Portfolio management</li>
            <li>Advanced analytics</li>
          </ul>
          <div className="billing-upgrade-actions">
            <Link to="/pricing" className="btn-billing-upgrade">
              View Plans & Pricing
            </Link>
          </div>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="billing-page authenticated-page">
        <div className="billing-container authenticated-page-content">
          <div className="billing-loading">
            <p>Loading your billing information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page authenticated-page">
      <div className="billing-container authenticated-page-content">
        <div className="billing-header">
          <h1 className="billing-title">Billing & Subscription</h1>
          <p className="billing-subtitle">
            Manage your plan, payment method, and subscription settings
          </p>
        </div>

        {error && (
          <div className="billing-error">
            {error}
          </div>
        )}

        {showSuccessBanner && (
          <div className="billing-success-banner">
            <div className="billing-success-icon-wrapper">
              <CheckCircleIcon />
            </div>
            <div className="billing-success-content">
              <h3 className="billing-success-title">Subscription activated successfully.</h3>
              <p className="billing-success-text">
                Stripe has confirmed your checkout. Your plan access will appear here once the subscription update is processed.
              </p>
            </div>
          </div>
        )}

        {showPortalBanner === 'subscription_updated' && (
          <div className="billing-portal-banner billing-portal-updated">
            <div className="billing-portal-icon-wrapper">
              <CheckCircleIcon />
            </div>
            <div className="billing-portal-content">
              <h3 className="billing-portal-title">Subscription updated successfully.</h3>
              <p className="billing-portal-text">
                Your subscription changes have been saved. Updates will appear here once processed by Stripe.
              </p>
            </div>
          </div>
        )}

        {showPortalBanner === 'subscription_canceled' && (
          <div className="billing-portal-banner billing-portal-canceled">
            <div className="billing-portal-icon-wrapper">
              <CheckCircleIcon />
            </div>
            <div className="billing-portal-content">
              <h3 className="billing-portal-title">Subscription cancellation processed.</h3>
              <p className="billing-portal-text">
                Your cancellation request has been submitted. You'll continue to have access until the end of your billing period.
              </p>
            </div>
          </div>
        )}

        {userInfo && (
          <>
            <div className="billing-plan-card">
              <div className="billing-plan-card-header">
                <span className="billing-plan-card-label">Current Plan</span>
              </div>

              <div className="billing-plan-card-body">
                <h2 className="billing-plan-name">{getPlanDisplay(userInfo.effective_plan || userInfo.plan_type)}</h2>

                {!hasActiveSubscription() && (
                  <div className="billing-no-subscription-notice">
                    <p className="billing-no-subscription-text">
                      You do not currently have an active paid subscription.
                    </p>
                  </div>
                )}

                {hasActiveSubscription() && (
                  <>
                    <div className="billing-plan-details">
                      {userInfo.billing_cycle && (
                        <div className="billing-detail-row">
                          <span className="billing-detail-label">Billing Cycle</span>
                          <span className="billing-detail-value">{getBillingCycleDisplay(userInfo.billing_cycle)}</span>
                        </div>
                      )}

                      {userInfo.stripe_subscription_status && (
                        <div className="billing-detail-row">
                          <span className="billing-detail-label">Status</span>
                          <span className="billing-detail-value">{getStatusDisplay(userInfo.stripe_subscription_status)}</span>
                        </div>
                      )}

                      {userInfo.stripe_current_period_end && !userInfo.stripe_cancel_at_period_end && (
                        <div className="billing-detail-row">
                          <span className="billing-detail-label">Next Billing Date</span>
                          <span className="billing-detail-value">{formatDate(userInfo.stripe_current_period_end)}</span>
                        </div>
                      )}

                      {userInfo.stripe_cancel_at_period_end && userInfo.stripe_current_period_end && (
                        <div className="billing-detail-row">
                          <span className="billing-detail-label">Access Ends</span>
                          <span className="billing-detail-value">{formatDate(userInfo.stripe_current_period_end)}</span>
                        </div>
                      )}
                    </div>

                    {userInfo.stripe_cancel_at_period_end && userInfo.stripe_current_period_end && (
                      <div className="billing-cancellation-notice">
                        <p className="billing-cancellation-text">
                          <strong>Cancellation scheduled.</strong> Your subscription will end on {formatDate(userInfo.stripe_current_period_end)}. You'll continue to have access until then.
                        </p>
                      </div>
                    )}

                    {userInfo.stripe_subscription_id && (
                      <div className="billing-actions">
                        <div className="billing-action-buttons">
                          <button
                            onClick={() => handleManageSubscription('subscription_update')}
                            disabled={portalLoading}
                            className="btn-billing-primary"
                          >
                            {portalLoading ? 'Loading...' : 'Change Plan'}
                          </button>
                          <button
                            onClick={() => handleManageSubscription('subscription_cancel')}
                            disabled={portalLoading}
                            className="btn-billing-secondary"
                          >
                            {portalLoading ? 'Loading...' : 'Cancel Subscription'}
                          </button>
                          <button
                            onClick={() => handleManageSubscription()}
                            disabled={portalLoading}
                            className="btn-billing-tertiary"
                          >
                            {portalLoading ? 'Loading...' : 'Manage Billing Details'}
                          </button>
                        </div>
                        <p className="billing-helper-text">
                          Change your plan, cancel your subscription, or update payment method and view invoices
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {renderUpgradeSection()}

          </>
        )}
      </div>
    </div>
  );
}

export default Billing;
